import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  TextField,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useMutation, gql } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { StyledDialogTitle } from './StyledComponents';

// Mutation pour mettre à jour le statut d'une visite
const UPDATE_VISIT_STATUS = gql`
  mutation UpdateVisitStatus($id: ID!, $status: String!, $rejectionReason: String) {
    updateVisit(id: $id, status: $status, rejectionReason: $rejectionReason) {
      success
      message
      visit {
        id
        status
        rejectionReason
        visitor {
          id
          firstName
          lastName
          email
        }
        host {
          id
          firstName
          lastName
          email
        }
        department {
          id
          name
        }
        purpose
        scheduledAt
      }
    }
  }
`;

function VisitDetails({ visit, open, onClose, onUpdate }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const visitorName = `${visit?.visitor?.firstName || ''} ${visit?.visitor?.lastName || ''}`.trim() || 'Visiteur';
  const visitorEmail = visit?.visitor?.email || 'Non renseigné';

  const [updateVisitStatus] = useMutation(UPDATE_VISIT_STATUS, {
    onCompleted: (data) => {
      if (data.updateVisit.success) {
        setSuccess(data.updateVisit.message);
        setError('');
        if (onUpdate) {
          onUpdate(data.updateVisit.visit);
        }
      } else {
        setError(data.updateVisit.message);
        setSuccess('');
      }
    },
    onError: (error) => {
      console.error('Erreur lors de la mise à jour du statut:', error);
      setError(error.message);
      setSuccess('');
    }
  });

  const handleStatusUpdate = async (newStatus) => {
    try {
      const variables = {
        id: visit.id,
        status: newStatus
      };

      // Ajouter le motif de rejet uniquement si le statut est REJECTED
      if (newStatus === 'REJECTED') {
        if (!rejectionReason.trim()) {
          setError('Le motif du rejet est obligatoire');
          return;
        }
        variables.rejectionReason = rejectionReason;
      }

      await updateVisitStatus({
        variables
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      setError(error.message);
    }
  };

  const canApprove = visit.status === 'PENDING';
  const canReject = visit.status === 'PENDING';
  const canComplete = visit.status === 'APPROVED';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>Détails de la visite</StyledDialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Visiteur</Typography>
            <Typography>
              {visitorName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {visitorEmail}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Hôte</Typography>
            <Typography>
              {visit.host.firstName} {visit.host.lastName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {visit.host.email}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Département</Typography>
            <Typography>{visit.department.name}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Date et heure</Typography>
            <Typography>
              {format(new Date(visit.scheduledAt), "PPP 'à' HH:mm", { locale: fr })}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Motif de la visite</Typography>
            <Typography>{visit.purpose}</Typography>
          </Grid>

          {!!visit?.documents?.length && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">Pièces jointes de la visite</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {visit.documents.map((doc) => (
                  <Button
                    key={doc.id}
                    component="a"
                    href={doc.documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                  >
                    {doc.fileName}
                  </Button>
                ))}
              </Box>
            </Grid>
          )}

          {!!visit?.appointment?.documents?.length && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">Pièces jointes du rendez-vous</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {visit.appointment.documents.map((doc) => (
                  <Button
                    key={doc.id}
                    component="a"
                    href={doc.documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                  >
                    {doc.fileName}
                  </Button>
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1">Statut</Typography>
            <Typography>
              {visit.status === 'PENDING' && 'En attente'}
              {visit.status === 'APPROVED' && 'Approuvée'}
              {visit.status === 'REJECTED' && 'Rejetée'}
              {visit.status === 'COMPLETED' && 'Terminée'}
            </Typography>
          </Grid>

          {visit.status === 'REJECTED' && visit.rejectionReason && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">Motif du rejet</Typography>
              <Typography color="error">{visit.rejectionReason}</Typography>
            </Grid>
          )}

          {canReject && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Motif du rejet"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
              />
            </Grid>
          )}

         <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
                <strong>Date de création:</strong> {new Date(visit.createdAt).toLocaleString()}
            </Typography>
         </Grid> 
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        {canApprove && (
          <Button 
            onClick={() => handleStatusUpdate('APPROVED')}
            color="success"
            variant="contained"
          >
            Approuver
          </Button>
        )}
        {canReject && (
          <Button 
            onClick={() => handleStatusUpdate('REJECTED')}
            color="error"
            variant="contained"
            disabled={!rejectionReason}
          >
            Rejeter
          </Button>
        )}
        {canComplete && (
          <Button 
            onClick={() => handleStatusUpdate('COMPLETED')}
            color="primary"
            variant="contained"
          >
            Terminer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default VisitDetails; 
